import { Button, Layout, Spin } from 'antd';
import { type ReactNode, type Ref, useState } from 'react';

import { NavMenu } from './NavMenu';
import { onKeyActivate } from './a11y';
import type { NavItem } from './types';

const { Header, Sider, Content } = Layout;

export interface AppShellProps {
  /** Menu shown at the top of the sidebar. */
  topMenu?: NavItem[];
  /** Secondary menu shown lower in the sidebar. */
  bottomMenu?: NavItem[];
  /** Keys of the currently active top-menu entries. */
  selectedKeys?: string[];
  /** Hide the whole sidebar (e.g. for admin/full-screen contexts). */
  hideSidebar?: boolean;
  /** Show a full-screen spinner instead of the sidebar contents. */
  sidebarLoading?: boolean;
  /** Expanded-state logo node (usually a link home). */
  logo?: ReactNode;
  /** Collapsed-state logo node. */
  collapsedLogo?: ReactNode;
  /** Icon for the "expand" state of the collapse toggle. */
  expandIcon?: ReactNode;
  /** Icon for the "collapse" state of the collapse toggle. */
  collapseIcon?: ReactNode;
  /** Header content aligned to the start. */
  headerLeft?: ReactNode;
  /** Header content aligned to the end. */
  headerRight?: ReactNode;
  /** Class applied to the content region. */
  contentClassName?: string;
  /** Ref to the scrollable content element. */
  contentRef?: Ref<HTMLDivElement>;
  /** Main content, typically the router `<Outlet />`. */
  children?: ReactNode;
  /** Class applied to the outer layout. */
  className?: string;
  /** Initial collapsed state of the sidebar. */
  defaultCollapsed?: boolean;
  /** Start collapsed when the viewport is at or below this width (px). */
  collapseBelowWidth?: number;
  siderWidth?: number;
  collapsedWidth?: number;
}

/** Whether the viewport is at or below `width` px. */
function shouldCollapseForWidth(width?: number): boolean {
  if (width == null) return false;
  return window.matchMedia(`(max-width: ${width}px)`).matches;
}

/**
 * Product-agnostic application chrome: a collapsible sidebar (logo + two
 * menus), a header with start/end slots, and a scrollable content region. It
 * carries no routing or domain logic — callers supply menu data and slots.
 */
export function AppShell({
  topMenu = [],
  bottomMenu = [],
  selectedKeys,
  hideSidebar = false,
  sidebarLoading = false,
  logo,
  collapsedLogo,
  expandIcon = '\u2261',
  collapseIcon = '\u00d7',
  headerLeft,
  headerRight,
  contentClassName,
  contentRef,
  children,
  className,
  defaultCollapsed = true,
  collapseBelowWidth,
  siderWidth = 240,
  collapsedWidth = 65,
}: Readonly<AppShellProps>) {
  const [collapsed, setCollapsed] = useState(
    () => shouldCollapseForWidth(collapseBelowWidth) || defaultCollapsed,
  );
  function toggle() {
    setCollapsed((c) => !c);
  }

  return (
    <Layout style={{ minHeight: '100vh' }} className={className}>
      {!hideSidebar && (
        <div className="print-hidden">
          {sidebarLoading ? (
            <Spin fullscreen />
          ) : (
            <Sider
              collapsible
              collapsed={collapsed}
              width={siderWidth}
              collapsedWidth={collapsedWidth}
              trigger={null}
              aria-label="Main navigation"
            >
              {logo && <div className="logo">{logo}</div>}
              {collapsedLogo && (
                <div
                  className="logo-collapsed"
                  role="button"
                  tabIndex={0}
                  onClick={toggle}
                  onKeyDown={(e) => onKeyActivate(e, toggle)}
                >
                  {collapsedLogo}
                </div>
              )}
              <Button
                className="SiderToggle"
                type="text"
                icon={collapsed ? expandIcon : collapseIcon}
                onClick={toggle}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                tabIndex={0}
                role="button"
                onKeyDown={(e) => onKeyActivate(e, toggle)}
              />
              <div className="sider-menu-wrapper">
                <NavMenu items={topMenu} selectedKeys={selectedKeys} />
                <button
                  type="button"
                  className="sider-toggle-spacer"
                  aria-label="Toggle sidebar"
                  onClick={toggle}
                />
                <NavMenu
                  className="ant-menu--mid-menu"
                  items={bottomMenu}
                  selectedKeys={[]}
                />
              </div>
            </Sider>
          )}
        </div>
      )}
      <Layout>
        {(headerLeft || headerRight) && (
          <Header className="app-header print-hidden">
            <div className="left-container">{headerLeft}</div>
            <div className="right-container">{headerRight}</div>
          </Header>
        )}
        <Content ref={contentRef} className={`app-content ${contentClassName}`}>
          <div className="content-wrapper">{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
}
